import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spin, Button, Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";

import { OptimisationReviewAPI } from "../../API/OptimisationreviewApi";
import { useFlightModel } from "../../contexts/FlightModelContext";
import { FILTER_INITIAL_OBJECT } from "../../screens/Rankings/RankingsUtils";
import { RankingFilters } from "./RankingFilter";

import {
  INITIAL_STATE,
  SUMMARY_PAGE_SIZE_DEFAULT,
  CHIP_PAGE_SIZE,
  getRowId,
  getRowYieldPercent,
  mergeRowsById,
  flattenReviewListToSummaryRows,
  numberOrFallback,
  mapApiItemToSummaryRow,
} from "./OptimisationReview/utils";

import SelectedSpecifications from "./OptimisationReview/SelectedSpecifications";
import CostBenefitAnalysis from "./OptimisationReview/CostBenefitAnalysis";
import SelectedSpecificationsSummary from "./OptimisationReview/SelectedSpecificationsSummary";
import OptimisationWorkflowForm from "./OptimisationReview/OptimisationWorkflowForm";
import OptimisationReviewFooter from "./OptimisationReview/OptimisationReviewFooter";

import "../../styles/OptimisationReview/OptimisationReview.css";

const { Text, Title } = Typography;

export default function OptimisationReview({
  open,
  setOpen,
  selectedRows = [],
  onRemoveSelectedRow,
  onAddSelectedRows,
  onClearAllSelectedRows,
  assigneeOptions = [],
  messageToast,
}) {
  const [formState, setFormState] = useState(INITIAL_STATE);
  const [reviewRows, setReviewRows] = useState([]);
  const [hasCalculated, setHasCalculated] = useState(false);
  // Flips the footer button label to "Recalculate Savings" once the user
  // changes a slider/toggle or a summary checkbox after calculating.
  const [needsRecalc, setNeedsRecalc] = useState(false);
  const [listSummaryRows, setListSummaryRows] = useState([]);
  const [summaryPage, setSummaryPage] = useState(1);
  const [summaryPageSize, setSummaryPageSize] = useState(
    SUMMARY_PAGE_SIZE_DEFAULT,
  );
  const [summarySearchQuery, setSummarySearchQuery] = useState("");
  const [summaryRowsState, setSummaryRowsState] = useState([]);
  const [summaryTotal, setSummaryTotal] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const [summarySelectedKeys, setSummarySelectedKeys] = useState([]);
  const [excludedRowIds, setExcludedRowIds] = useState([]);
  const [openReviewFilter, setOpenReviewFilter] = useState(false);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const searchRequestIdRef = useRef(0);
  const searchTimerRef = useRef(null);
  const [reviewFilterValues, setReviewFilterValues] = useState(
    FILTER_INITIAL_OBJECT,
  );
  const [reviewId, setReviewId] = useState(null);
  const { selectedFlightModel, refreshFlightModels } = useFlightModel();

  const selectedCount = reviewRows.length;
  const hasSelectedRows = selectedCount > 0;
  const slidersEnabled = hasSelectedRows;

  const assignToOptions = useMemo(
    () =>
      (assigneeOptions || []).filter(
        (option) => option?.value !== null && option?.value !== undefined,
      ),
    [assigneeOptions],
  );

  const visibleSelectedRows = reviewRows;

  const previewChipRows = useMemo(
    () => visibleSelectedRows.slice(0, CHIP_PAGE_SIZE),
    [visibleSelectedRows],
  );

  const hasMoreChips = visibleSelectedRows.length > CHIP_PAGE_SIZE;

  useEffect(() => {
    if (visibleSelectedRows.length <= CHIP_PAGE_SIZE) {
      setViewAllOpen(false);
    }
  }, [visibleSelectedRows.length]);

  // Prefer GET /optimisation-review/list/ rows; fall back to calculate draft.
  const summaryRows =
    listSummaryRows.length > 0 ? listSummaryRows : summaryRowsState;
  const showSummarySection = hasCalculated;

  useEffect(() => {
    const maxPage = Math.max(
      1,
      Math.ceil((listSummaryRows.length > 0 ? listSummaryRows.length : summaryTotal) / summaryPageSize) || 1,
    );
    if (summaryPage > maxPage) {
      setSummaryPage(maxPage);
    }
  }, [summaryPage, summaryPageSize, summaryTotal, listSummaryRows.length]);

  useEffect(() => {
    setSummaryPage(1);
  }, [summaryTotal, listSummaryRows.length, hasCalculated]);

  const allSummarySelected = useMemo(
    () =>
      summaryRows.length > 0 &&
      summaryRows.length === summarySelectedKeys.length &&
      summaryRows.every((row) => summarySelectedKeys.includes(row.key)),
    [summaryRows, summarySelectedKeys],
  );

  const resetFormMetrics = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      optimisationPercent: INITIAL_STATE.optimisationPercent,
      yieldPercent: INITIAL_STATE.yieldPercent,
      yieldEnabled: INITIAL_STATE.yieldEnabled,
      currentRepeat: INITIAL_STATE.currentRepeat,
      optimisedRepeat: INITIAL_STATE.optimisedRepeat,
      currentLabourCost: INITIAL_STATE.currentLabourCost,
      optimisedLabourCost: INITIAL_STATE.optimisedLabourCost,
      projectedSavings: INITIAL_STATE.projectedSavings,
      expectedSavings: INITIAL_STATE.expectedSavings,
      savingsPercentage: INITIAL_STATE.savingsPercentage,
      estimatedAnnualSavings: INITIAL_STATE.estimatedAnnualSavings,
    }));
  }, []);

  const resetCalculationInputs = useCallback(() => {
    setFormState((prev) => ({
      ...INITIAL_STATE,
      search: prev.search,
      yieldPercent: prev.yieldPercent,
      optimisationStatus: prev.optimisationStatus,
      assignTo: prev.assignTo,
      dueDate: prev.dueDate,
      priority: prev.priority,
    }));
    setHasCalculated(false);
    setNeedsRecalc(false);
    setListSummaryRows([]);
    setSummaryPage(1);
    setSummarySelectedKeys([]);
    setReviewId(null);
    setSummarySearchQuery("");
    setSummaryRowsState([]);
    setSummaryTotal(0);
  }, []);

  // Clears the Selected Specifications search box: drops the typed text, the
  // pending debounce and any in-flight response, so a resolving search can't
  // reopen the dropdown after the user has already cleared the review.
  const resetSearchState = useCallback(() => {
    searchRequestIdRef.current += 1;
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    setSearchQuery("");
    setSearchResults([]);
    setSearchLoading(false);
    setSearchDropdownOpen(false);
  }, []);

  const closeModal = useCallback(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    setOpen(false);
    setOpenReviewFilter(false);
    setViewAllOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    setSearchDropdownOpen(false);
    setFormState(INITIAL_STATE);
    setReviewRows([]);
    setHasCalculated(false);
    setNeedsRecalc(false);
    setListSummaryRows([]);
    setSummaryPage(1);
    setSummaryPageSize(SUMMARY_PAGE_SIZE_DEFAULT);
    setSummarySelectedKeys([]);
    setSummaryExpanded(true);
    setReviewFilterValues(FILTER_INITIAL_OBJECT);
    setReviewId(null);
    setSummarySearchQuery("");
    setSummaryRowsState([]);
    setSummaryTotal(0);
  }, [setOpen]);

  // Adding/removing a spec chip changes the underlying set, so the calculated
  // grid is discarded and the button returns to "Calculate Savings".
  const markDirty = useCallback(() => {
    if (hasCalculated) {
      setHasCalculated(false);
      setListSummaryRows([]);
      setSummarySelectedKeys([]);
      setSummaryRowsState([]);
      setSummaryTotal(0);
      setSummarySearchQuery("");
      setNeedsRecalc(false);
    }
  }, [hasCalculated]);

  // Slider / yield-toggle change: keep the grid visible; just flag that a
  // recalculation (or apply-to-checked) is pending.
  const handleSlidersDirty = useCallback(() => {
    if (hasCalculated) {
      setNeedsRecalc(true);
    }
  }, [hasCalculated]);

  // Summary checkbox changes only pick which rows an action targets — they do
  // not invalidate the calculated figures, so the button label stays put.

  const getFlightModel = useCallback(async () => {
    let flightModelToUse = selectedFlightModel;
    if (!flightModelToUse) {
      const refreshed = await refreshFlightModels();
      flightModelToUse = refreshed?.selectedFlightModel || null;
    }
    return flightModelToUse;
  }, [selectedFlightModel, refreshFlightModels]);

  const fetchReviewList = useCallback(
    async ({
      preferredReviewId = null,
      rankedIds = null,
      silent = false,
    } = {}) => {
      const flightModelToUse = await getFlightModel();
      if (!flightModelToUse) {
        if (!silent) {
          messageToast?.("error", "No flight model selected.");
        }
        return [];
      }

      try {
        const res = await OptimisationReviewAPI.list({
          params: {
            flight_model: flightModelToUse,
            page: 1,
            page_size: 50,
          },
        });

        const payload = res?.data?.status !== undefined ? res.data : res;
        if (payload?.status !== true) {
          if (!silent) {
            messageToast?.(
              "error",
              payload?.message || res?.statusText || "Failed to load reviews.",
            );
          }
          return [];
        }

        const reviews = Array.isArray(payload?.data) ? payload.data : [];
        let rows = flattenReviewListToSummaryRows(
          reviews,
          preferredReviewId,
        );

        if (Array.isArray(rankedIds) && rankedIds.length > 0) {
          const allowed = new Set(
            rankedIds.map(Number).filter((id) => Number.isFinite(id)),
          );
          rows = rows.filter((row) => allowed.has(Number(row.rankedId)));
        }

        setListSummaryRows(rows);
        if (rows.length > 0) {
          setSummarySelectedKeys(rows.map((row) => row.key));
        }
        return rows;
      } catch (error) {
        if (!silent) {
          messageToast?.(
            "error",
            error?.message || "Failed to load reviews.",
          );
        }
        return [];
      }
    },
    [messageToast, getFlightModel],
  );

  const fetchSummaryRows = useCallback(
    async (page, pageSize, searchStr) => {
      const flightModelToUse = await getFlightModel();
      if (!flightModelToUse) {
        messageToast?.("error", "No flight model selected.");
        return;
      }

      if (!reviewId) return;

      setSpinning(true);
      try {
        const res = await OptimisationReviewAPI.detail({
          flight_model: flightModelToUse,
          review_id: reviewId,
          page,
          page_size: pageSize,
          search: searchStr,
        });

        const payload = res?.data?.status !== undefined ? res.data : res;
        if (payload?.status !== true) {
          throw new Error(
            payload?.message || res?.statusText || "Failed to load calculated summary.",
          );
        }

        const rowResults = Array.isArray(payload?.items) ? payload.items : [];
        const reviewDetails = payload?.review || {};
        const total = payload?.total || 0;

        const mappedRows = rowResults.map((item, index) =>
          mapApiItemToSummaryRow(item, index)
        );

        setSummaryRowsState(mappedRows);
        setSummaryTotal(total);

        const totalProjected = Number(reviewDetails.total_projected_savings) || 0;
        const totalExpected = Number(reviewDetails.total_expected_savings) || 0;
        const totalLabour = Number(reviewDetails.total_current_labor_cost) || 0;
        const savingsPercentage = Number(reviewDetails.total_savings_percentage) || 0;
        const estimatedAnnual = numberOrFallback(
          reviewDetails.estimated_annual_savings,
          totalProjected
        );

        setFormState((prev) => ({
          ...prev,
          currentLabourCost: totalLabour,
          optimisedLabourCost: Math.max(totalLabour - totalProjected, 0),
          projectedSavings: totalProjected,
          expectedSavings: totalExpected,
          savingsPercentage,
          estimatedAnnualSavings: estimatedAnnual,
        }));
      } catch (error) {
        messageToast?.(
          "error",
          error?.message || "Failed to load calculated summary.",
        );
      } finally {
        setSpinning(false);
      }
    },
    [
      reviewId,
      getFlightModel,
      messageToast,
    ],
  );

  const calculateSavings = useCallback(async () => {
    if (!hasSelectedRows) {
      messageToast?.(
        "warning",
        "Select or search for one or more specifications first.",
      );
      return;
    }

    const flightModelToUse = await getFlightModel();
    if (!flightModelToUse) {
      messageToast?.("error", "No flight model selected.");
      return;
    }

    // The Status / Assignment / Justification form only appears after the first
    // calculation, so those fields have no user-chosen values yet. Send them as
    // null on the initial calculate; on a recalculate carry the user's edits.
    const isInitialCalculate = !hasCalculated;
    const dueDate =
      formState.dueDate && typeof formState.dueDate.format === "function"
        ? formState.dueDate.format("YYYY-MM-DD")
        : formState.dueDate || null;

    const items = reviewRows
      .map((row) => {
        const rankedId = Number(row?.id ?? row?.ranked_id ?? row?.key);
        if (!Number.isFinite(rankedId)) return null;
        return {
          spec_concat: row?.spec_concat || row?.spec || "",
          card_no: row?.card_no || "",
          ranked_id: rankedId,
          optimisation: formState.optimisationPercent,
          yield_toggle: Boolean(formState.yieldEnabled),
          // Backend uses grid yield when toggle is off; send 0 in that case.
          yield_value: formState.yieldEnabled ? formState.yieldPercent : 0,
          status: isInitialCalculate ? null : formState.optimisationStatus,
          assigned_to: isInitialCalculate ? null : formState.assignTo ?? null,
          due_date: isInitialCalculate ? null : dueDate,
          priority: isInitialCalculate ? null : formState.priority,
          business_justification: formState.justification,
          engineering_observation: formState.engineeringObservations,
          maintenance_considerations: formState.maintenanceConsiderations,
          supporting_notes: formState.supportingNotes,
        };
      })
      .filter(Boolean);

    if (items.length === 0) {
      messageToast?.(
        "error",
        "Selected specifications are missing ranking IDs.",
      );
      return;
    }

    setSpinning(true);
    try {
      const res = await OptimisationReviewAPI.calculate({
        page: 1,
        page_size: summaryPageSize,
        search: summarySearchQuery,
        flight_model: flightModelToUse,
        items,
        review_id: reviewId,
      });

      const payload = res?.data?.status !== undefined ? res.data : res;
      if (payload?.status !== true) {
        throw new Error(
          payload?.message || res?.statusText || "Failed to calculate savings.",
        );
      }

      if (payload?.review_id) {
        setReviewId(payload.review_id);
      }

      const rowResults = Array.isArray(payload?.data) ? payload.data : [];
      const summary = payload?.summary || {};
      const total = payload?.total || 0;

      const mappedRows = rowResults.map((item, index) =>
        mapApiItemToSummaryRow(item, index)
      );

      setSummaryRowsState(mappedRows);
      setSummaryTotal(total);
      setSummaryPage(1);

      const totalProjected = Number(summary.total_projected_savings) || 0;
      const totalExpected = Number(summary.total_expected_savings) || 0;
      const totalLabour = Number(summary.total_current_labor_cost) || 0;
      const savingsPercentage = Number(summary.total_savings_percentage) || 0;
      const estimatedAnnual = numberOrFallback(
        summary.estimated_annual_savings,
        totalProjected
      );

      setHasCalculated(true);
      setNeedsRecalc(false);
      // Rows start unchecked; the user picks which ones to act on.
      setSummarySelectedKeys([]);
      setFormState((prev) => ({
        ...prev,
        currentLabourCost: totalLabour,
        optimisedLabourCost: Math.max(totalLabour - totalProjected, 0),
        projectedSavings: totalProjected,
        expectedSavings: totalExpected,
        savingsPercentage,
        estimatedAnnualSavings: estimatedAnnual,
      }));

      messageToast?.("success", "Savings calculated successfully.");
    } catch (error) {
      messageToast?.(
        "error",
        error?.message || "Failed to calculate savings.",
      );
    } finally {
      setSpinning(false);
    }
  }, [
    hasSelectedRows,
    getFlightModel,
    messageToast,
    hasCalculated,
    formState,
    reviewRows,
    summaryPageSize,
    summarySearchQuery,
    reviewId,
  ]);

  // Apply the current Optimization/Yield sliders to the checked summary rows
  // only. Totals re-aggregate over the whole review; per-row applied values are
  // refreshed from the backend. Used when the button is clicked with rows
  // checked.
  const applyOptimisationToChecked = useCallback(async () => {
    const flightModelToUse = await getFlightModel();
    if (!flightModelToUse) {
      messageToast?.("error", "No flight model selected.");
      return;
    }
    if (!reviewId) {
      messageToast?.("warning", "Calculate savings first.");
      return;
    }

    const checkedRows = summaryRows.filter((row) =>
      summarySelectedKeys.includes(row.key),
    );
    const itemIds = checkedRows.map((row) => row.itemId).filter(Boolean);
    if (itemIds.length === 0) {
      messageToast?.("warning", "No checked rows to apply.");
      return;
    }

    setSpinning(true);
    try {
      const res = await OptimisationReviewAPI.applyOptimisation({
        review_id: reviewId,
        flight_model: flightModelToUse,
        item_ids: itemIds,
        optimisation: formState.optimisationPercent,
        yield_toggle: Boolean(formState.yieldEnabled),
        // yield_value is required only when the toggle is on; otherwise the
        // backend keeps each row's grid yield.
        ...(formState.yieldEnabled
          ? { yield_value: formState.yieldPercent }
          : {}),
      });

      const payload = res?.data?.status !== undefined ? res.data : res;
      if (payload?.status !== true) {
        throw new Error(
          payload?.message || res?.statusText || "Failed to apply optimisation.",
        );
      }

      const summary = payload?.summary || {};
      const totalProjected = Number(summary.total_projected_savings) || 0;
      const totalExpected = Number(summary.total_expected_savings) || 0;
      const totalLabour = Number(summary.total_current_labor_cost) || 0;
      const savingsPercentage = Number(summary.total_savings_percentage) || 0;
      const estimatedAnnual = numberOrFallback(
        summary.estimated_annual_savings,
        totalProjected
      );

      setFormState((prev) => ({
        ...prev,
        currentLabourCost: totalLabour,
        optimisedLabourCost: Math.max(totalLabour - totalProjected, 0),
        projectedSavings: totalProjected,
        expectedSavings: totalExpected,
        savingsPercentage,
        estimatedAnnualSavings: estimatedAnnual,
      }));

      // Refresh the current page so Applied Optimization / Applied Yield /
      // Projected Savings reflect the newly applied values.
      await fetchSummaryRows(summaryPage, summaryPageSize, summarySearchQuery);

      setNeedsRecalc(false);
      messageToast?.("success", payload.message || "Applied to selected rows.");
    } catch (error) {
      messageToast?.(
        "error",
        error?.message || "Failed to apply optimisation.",
      );
    } finally {
      setSpinning(false);
    }
  }, [
    getFlightModel,
    messageToast,
    reviewId,
    summaryRows,
    summarySelectedKeys,
    formState.optimisationPercent,
    formState.yieldEnabled,
    formState.yieldPercent,
    fetchSummaryRows,
    summaryPage,
    summaryPageSize,
    summarySearchQuery,
  ]);

  // Footer button dispatcher: with rows checked, apply the sliders to just
  // those rows (new API); otherwise (re)calculate the whole selection (old API).
  const handleCalculateOrApply = useCallback(() => {
    if (hasCalculated && reviewId && summarySelectedKeys.length > 0) {
      applyOptimisationToChecked();
    } else {
      calculateSavings();
    }
  }, [hasCalculated, reviewId, summarySelectedKeys.length, applyOptimisationToChecked, calculateSavings]);

  useEffect(() => {
    if (hasCalculated && reviewId) {
      fetchSummaryRows(summaryPage, summaryPageSize, summarySearchQuery);
    }
  }, [summaryPage, summaryPageSize, summarySearchQuery, hasCalculated, reviewId, fetchSummaryRows]);

  const runLiveSearch = useCallback(
    async (query) => {
      const trimmed = query.trim();
      if (trimmed.length < 2) {
        setSearchResults([]);
        setSearchLoading(false);
        setSearchDropdownOpen(false);
        return;
      }

      const requestId = ++searchRequestIdRef.current;
      setSearchLoading(true);

      try {
        const flightModelToUse = await getFlightModel();
        if (!flightModelToUse) {
          if (requestId === searchRequestIdRef.current) {
            setSearchResults([]);
            setSearchLoading(false);
            messageToast?.("error", "No flight model selected.");
          }
          return;
        }

        const res = await OptimisationReviewAPI.search({
          flight_model: flightModelToUse,
          queries: [trimmed],
        });

        if (requestId !== searchRequestIdRef.current) return;

        // Supports { data: { data, queries, status } } and { data, queries, status }
        const payload = res?.data?.status !== undefined ? res.data : res;
        const matches = Array.isArray(payload?.data) ? payload.data : [];

        if (payload?.status !== true) {
          setSearchResults([]);
          setSearchDropdownOpen(true);
          return;
        }

        setSearchResults(matches);
        setSearchDropdownOpen(true);
      } catch {
        if (requestId === searchRequestIdRef.current) {
          setSearchResults([]);
          messageToast?.("error", "Search failed. Please try again.");
        }
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setSearchLoading(false);
        }
      }
    },
    [messageToast, getFlightModel],
  );

  const searchOptions = useMemo(() => {
    if (!searchResults || searchResults.length === 0) return [];
    const selectedIds = new Set(reviewRows.map(getRowId));
    return searchResults.map((row) => {
      const rowId = getRowId(row);
      const spec = row?.spec_concat || row?.spec || "Specification";
      const cardNo = row?.card_no || "";
      const alreadySelected = selectedIds.has(rowId);
      return {
        value: rowId,
        label: (
          <div className="optimisation-review__search-option">
            <div className="optimisation-review__search-option-main">
              <span className="optimisation-review__search-option-spec">
                {spec}
              </span>
              {cardNo ? (
                <span className="optimisation-review__search-option-card">
                  {cardNo}
                </span>
              ) : null}
            </div>
            {row?.task_description ? (
              <span className="optimisation-review__search-option-task">
                {row.task_description}
              </span>
            ) : null}
            {alreadySelected ? (
              <span className="optimisation-review__search-option-tag">
                Selected
              </span>
            ) : null}
          </div>
        ),
        row,
        disabled: alreadySelected,
      };
    });
  }, [searchResults, reviewRows]);

  const handleSummarySearch = useCallback((query) => {
    setSummarySearchQuery(query ?? "");
    setSummaryPage(1);
  }, []);

  const handleLiveSearchChange = (value) => {
    const nextValue = value ?? "";
    setSearchQuery(nextValue);

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (!nextValue.trim()) {
      searchRequestIdRef.current += 1;
      setSearchResults([]);
      setSearchLoading(false);
      setSearchDropdownOpen(false);
      return;
    }

    searchTimerRef.current = setTimeout(() => {
      runLiveSearch(nextValue);
    }, 350);
  };

  const handleSelectSearchOption = (_value, option) => {
    const row = option?.row;
    if (!row) return;

    setReviewRows((prev) => mergeRowsById(prev, [row]));
    setExcludedRowIds((prev) => prev.filter((id) => id !== getRowId(row)));
    markDirty();
    setSearchQuery("");
    setSearchResults([]);
    setSearchDropdownOpen(false);
    messageToast?.(
      "success",
      `${row?.spec_concat || row?.spec || "Specification"} added.`,
    );
  };

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  const handleRemoveChip = useCallback((row) => {
    const rowId = getRowId(row);
    setExcludedRowIds((prev) =>
      prev.includes(rowId) ? prev : [...prev, rowId],
    );
    setReviewRows((prev) => {
      const nextRows = prev.filter((item) => getRowId(item) !== rowId);
      if (nextRows.length === 0) {
        // Removing the last chip empties the review just like "Clear all", so
        // the Filters modal must forget its criteria too.
        setReviewFilterValues(FILTER_INITIAL_OBJECT);
        resetFormMetrics();
      }
      return nextRows;
    });
    onRemoveSelectedRow?.(row);
    markDirty();
    messageToast?.(
      "success",
      `${row?.spec_concat || row?.spec || "Specification"} removed.`,
    );
  }, [onRemoveSelectedRow, markDirty, messageToast, resetFormMetrics]);

  const handleClearAll = useCallback(() => {
    const idsToExclude = reviewRows.map(getRowId);
    setExcludedRowIds((prev) => [...new Set([...prev, ...idsToExclude])]);
    setReviewRows([]);
    setViewAllOpen(false);
    // Specs may have been loaded from the review's Filters modal, so clearing
    // the chips must also drop those filter selections — otherwise reopening
    // Filters still shows the old criteria for a now-empty selection.
    setReviewFilterValues(FILTER_INITIAL_OBJECT);
    setHasCalculated(false);
    setNeedsRecalc(false);
    setSummarySelectedKeys([]);
    resetSearchState();
    resetFormMetrics();
    onClearAllSelectedRows?.();
    messageToast?.("success", "All selected specifications cleared.");
  }, [reviewRows, resetSearchState, resetFormMetrics, onClearAllSelectedRows, messageToast]);

  const handleCancel = useCallback(() => {
    // Nothing to reset when there are no specs and no calculation yet — skip
    // the toast so Cancel doesn't report clearing an already-empty review.
    const hasSomethingToClear = reviewRows.length > 0 || hasCalculated;

    resetCalculationInputs();
    // Also clear the Selected Specifications so cancelling resets the whole
    // review (chips + underlying ranking-grid selection), not just the
    // calculation inputs.
    const idsToExclude = reviewRows.map(getRowId);
    setExcludedRowIds((prev) => [...new Set([...prev, ...idsToExclude])]);
    setReviewRows([]);
    setViewAllOpen(false);
    setReviewFilterValues(FILTER_INITIAL_OBJECT);
    resetSearchState();
    resetFormMetrics();
    onClearAllSelectedRows?.();
    if (hasSomethingToClear) {
      messageToast?.("success", "Calculation inputs cleared.");
    }
  }, [reviewRows, hasCalculated, resetCalculationInputs, resetSearchState, resetFormMetrics, onClearAllSelectedRows, messageToast]);

  // Checked summary rows are the target for the per-section apply APIs.
  const getCheckedSummaryRows = useCallback(
    () => summaryRows.filter((row) => summarySelectedKeys.includes(row.key)),
    [summaryRows, summarySelectedKeys],
  );

  const getCheckedItemIds = useCallback(
    (checkedRows) =>
      checkedRows
        .map((row) => Number(row?.itemId ?? row?.row?.id))
        .filter((id) => Number.isFinite(id)),
    [],
  );

  const resolveReviewId = useCallback(
    (checkedRows) =>
      reviewId ?? checkedRows.find((row) => row?.reviewId != null)?.reviewId ?? null,
    [reviewId],
  );

  // Break onto a second line only when the field list makes the toast long.
  const buildMissingFieldsMessage = useCallback((fields) => {
    const list = fields.join(", ");
    const prefix = "Please fill in the following field(s):";
    if (`${prefix} ${list}.`.length <= 60) {
      return `${prefix} ${list}.`;
    }
    return (
      <>
        {prefix}
        <br />
        {list}.
      </>
    );
  }, []);

  const handleStatusChange = useCallback(async (value) => {
    setFormState((prev) => ({
      ...prev,
      optimisationStatus: value,
    }));

    const checkedRows = getCheckedSummaryRows();
    if (checkedRows.length === 0) return;

    const flightModelToUse = await getFlightModel();
    if (!flightModelToUse) {
      messageToast?.("error", "No flight model selected.");
      return;
    }

    setSpinning(true);
    try {
      const res = await OptimisationReviewAPI.applyStatus({
        review_id: resolveReviewId(checkedRows),
        flight_model: flightModelToUse,
        item_ids: getCheckedItemIds(checkedRows),
        optimisation_status: value,
      });

      const payload = res?.data?.status !== undefined ? res.data : res;
      if (payload?.status !== true) {
        throw new Error(
          payload?.message || res?.statusText || "Failed to update status.",
        );
      }

      const checkedKeys = new Set(checkedRows.map((row) => row.key));
      setSummaryRowsState((prev) =>
        prev.map((row) =>
          checkedKeys.has(row.key) ? { ...row, status: value } : row,
        ),
      );

      messageToast?.("success", payload?.message || "Status updated.");
    } catch (error) {
      messageToast?.("error", error?.message || "Failed to update status.");
    } finally {
      setSpinning(false);
    }
  }, [getCheckedSummaryRows, getFlightModel, resolveReviewId, getCheckedItemIds, messageToast]);

  const handleSaveAssignment = useCallback(async () => {
    const checkedRows = getCheckedSummaryRows();
    if (checkedRows.length === 0) {
      messageToast?.("warning", "Select one or more rows to assign.");
      return;
    }

    const missingAssignmentFields = [];
    if (!formState.assignTo) missingAssignmentFields.push("Assign To");
    if (!formState.dueDate) missingAssignmentFields.push("Due Date");
    if (!formState.priority) missingAssignmentFields.push("Priority");
    if (missingAssignmentFields.length > 0) {
      messageToast?.("warning", buildMissingFieldsMessage(missingAssignmentFields));
      return;
    }

    const dueDateStr = formState.dueDate
      ? formState.dueDate.format?.("YYYY-MM-DD") || String(formState.dueDate)
      : null;

    const flightModelToUse = await getFlightModel();
    if (!flightModelToUse) {
      messageToast?.("error", "No flight model selected.");
      return;
    }

    setSpinning(true);
    try {
      const res = await OptimisationReviewAPI.applyAssignment({
        review_id: resolveReviewId(checkedRows),
        flight_model: flightModelToUse,
        item_ids: getCheckedItemIds(checkedRows),
        assigned_to: formState.assignTo ?? "",
        due_date: dueDateStr,
        priority: formState.priority,
      });

      const payload = res?.data?.status !== undefined ? res.data : res;
      if (payload?.status !== true) {
        throw new Error(
          payload?.message || res?.statusText || "Failed to update assignment.",
        );
      }

      messageToast?.("success", payload?.message || "Assignment updated.");
    } catch (error) {
      messageToast?.(
        "error",
        error?.message || "Failed to update assignment.",
      );
    } finally {
      setSpinning(false);
    }
  }, [getCheckedSummaryRows, formState.assignTo, formState.dueDate, formState.priority, buildMissingFieldsMessage, getFlightModel, resolveReviewId, getCheckedItemIds, messageToast]);

  const handleClearAssignment = useCallback(() => {
    // Nothing entered means nothing to clear, so skip the toast rather than
    // report success for a no-op.
    const hasSomethingToClear =
      formState.assignTo != null ||
      formState.dueDate != null ||
      formState.priority !== INITIAL_STATE.priority;

    setFormState((prev) => ({
      ...prev,
      assignTo: INITIAL_STATE.assignTo,
      dueDate: INITIAL_STATE.dueDate,
      priority: INITIAL_STATE.priority,
    }));

    if (hasSomethingToClear) {
      messageToast?.("success", "Assignment inputs cleared.");
    }
  }, [formState.assignTo, formState.dueDate, formState.priority, messageToast]);

  const handleSaveJustification = useCallback(async () => {
    const checkedRows = getCheckedSummaryRows();
    if (checkedRows.length === 0) {
      messageToast?.("warning", "Select one or more rows to add justification.");
      return;
    }

    const missingJustificationFields = [];
    if (!formState.justification?.trim())
      missingJustificationFields.push("Business Justification");
    if (!formState.engineeringObservations?.trim())
      missingJustificationFields.push("Engineering Observations");
    if (!formState.maintenanceConsiderations?.trim())
      missingJustificationFields.push("Maintenance Considerations");
    if (!formState.supportingNotes?.trim())
      missingJustificationFields.push("Supporting Notes");
    if (missingJustificationFields.length > 0) {
      messageToast?.("warning", buildMissingFieldsMessage(missingJustificationFields));
      return;
    }

    const flightModelToUse = await getFlightModel();
    if (!flightModelToUse) {
      messageToast?.("error", "No flight model selected.");
      return;
    }

    setSpinning(true);
    try {
      const res = await OptimisationReviewAPI.applyJustification({
        review_id: resolveReviewId(checkedRows),
        flight_model: flightModelToUse,
        item_ids: getCheckedItemIds(checkedRows),
        business_justification: formState.justification,
        engineering_observation: formState.engineeringObservations,
        maintenance_considerations: formState.maintenanceConsiderations,
        supporting_notes: formState.supportingNotes,
      });

      const payload = res?.data?.status !== undefined ? res.data : res;
      if (payload?.status !== true) {
        throw new Error(
          payload?.message ||
            res?.statusText ||
            "Failed to update justification.",
        );
      }

      messageToast?.("success", payload?.message || "Justification updated.");
    } catch (error) {
      messageToast?.(
        "error",
        error?.message || "Failed to update justification.",
      );
    } finally {
      setSpinning(false);
    }
  }, [getCheckedSummaryRows, formState.justification, formState.engineeringObservations, formState.maintenanceConsiderations, formState.supportingNotes, buildMissingFieldsMessage, getFlightModel, resolveReviewId, getCheckedItemIds, messageToast]);

  const handleClearJustification = useCallback(() => {
    // Nothing entered means nothing to clear, so skip the toast rather than
    // report success for a no-op. Whitespace-only counts as empty, matching the
    // trim() check Save uses before it accepts these fields.
    const hasSomethingToClear = [
      formState.justification,
      formState.engineeringObservations,
      formState.maintenanceConsiderations,
      formState.supportingNotes,
    ].some((value) => Boolean(value?.trim()));

    setFormState((prev) => ({
      ...prev,
      justification: INITIAL_STATE.justification,
      engineeringObservations: INITIAL_STATE.engineeringObservations,
      maintenanceConsiderations: INITIAL_STATE.maintenanceConsiderations,
      supportingNotes: INITIAL_STATE.supportingNotes,
    }));

    if (hasSomethingToClear) {
      messageToast?.("success", "Justification inputs cleared.");
    }
  }, [formState.justification, formState.engineeringObservations, formState.maintenanceConsiderations, formState.supportingNotes, messageToast]);

  const downloadReport = useCallback(async (format) => {
    if (!hasCalculated || !reviewId) {
      messageToast?.(
        "warning",
        "Calculate savings before downloading the report.",
      );
      return;
    }

    const flightModelToUse = await getFlightModel();
    if (!flightModelToUse) {
      messageToast?.("error", "No flight model selected.");
      return;
    }

    const checkedRows = summaryRows.filter((row) => summarySelectedKeys.includes(row.key));
    const itemIds = checkedRows.map((row) => row.itemId).filter(Boolean);

    // item_ids is optional: omit it to download the whole review; send it only
    // to restrict the report to the checked-row subset.
    const reportBody = {
      review_id: reviewId,
      flight_model: flightModelToUse,
      format: format,
    };
    if (itemIds.length > 0) {
      reportBody.item_ids = itemIds;
    }

    setSpinning(true);
    try {
      const response = await OptimisationReviewAPI.downloadReport(reportBody);

      const blob = new Blob([response.data], {
        type:
          format === "csv"
            ? "text/csv"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const contentDisposition = response.headers?.["content-disposition"];
      let fileName = `Optimisation_Review_${reviewId}.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(
          /filename[^;=\n]*=(['"]?)([^'"\n;]*)\1/,
        );
        if (match?.[2]) fileName = match[2].trim();
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      messageToast?.("success", `Report downloaded as ${format.toUpperCase()}.`);
    } catch (error) {
      messageToast?.("error", "Failed to download report.");
    } finally {
      setSpinning(false);
    }
  }, [hasCalculated, reviewId, getFlightModel, summaryRows, summarySelectedKeys, messageToast]);

  useEffect(() => {
    if (!open) return;

    const seededRows = selectedRows || [];
    setReviewRows(seededRows);
    setHasCalculated(false);
    setNeedsRecalc(false);
    setListSummaryRows([]);
    setSummaryPage(1);
    setSummaryPageSize(SUMMARY_PAGE_SIZE_DEFAULT);
    setSummarySelectedKeys([]);
    setSummaryExpanded(true);
    setExcludedRowIds([]);
    setReviewFilterValues(FILTER_INITIAL_OBJECT);
    setViewAllOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    setSearchDropdownOpen(false);
    setReviewId(null);

    const primary = seededRows[0];
    const yieldPercent = primary ? getRowYieldPercent(primary) : 75;
    setFormState({
      ...INITIAL_STATE,
      yieldPercent,
      currentRepeat: primary?.repeat_max || primary?.initial_max || 0,
      currentLabourCost: Number(primary?.total_labor_cost_max) || 0,
    });
    // Seed only when modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const excluded = new Set(excludedRowIds);
    const incoming = (selectedRows || []).filter(
      (row) => !excluded.has(getRowId(row)),
    );
    setReviewRows((prev) => mergeRowsById(prev, incoming));
  }, [open, selectedRows, excludedRowIds]);

  useEffect(() => {
    const validKeys = new Set(summaryRows.map((row) => row.key));
    setSummarySelectedKeys((prev) => prev.filter((key) => validKeys.has(key)));
  }, [summaryRows]);

  return (
    <Modal
      open={open}
      onCancel={closeModal}
      footer={null}
      width="95%"
      style={{ maxWidth: "1360px" }}
      centered
      destroyOnClose
      closable={false}
      zIndex={1000}
      className="optimisation-review-modal"
      styles={{
        body: { padding: 0, maxHeight: "90vh", overflow: "hidden" },
      }}
    >
      <div className="optimisation-review">
        <div className="optimisation-review__header">
          <div>
            <Title level={4} className="optimisation-review__title">
              Optimization Review
            </Title>
            <Text className="optimisation-review__subtitle">
              Review, adjust and submit inspection interval optimisations.
            </Text>
          </div>
          <div className="optimisation-review__header-actions">
            <span className="optimisation-review__spec-badge">
              {selectedCount} Specification{selectedCount === 1 ? "" : "s"}
            </span>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={closeModal}
              aria-label="Close"
            />
          </div>
        </div>

        <Spin
          spinning={spinning}
          className="optimisation-review__spin"
          wrapperClassName="optimisation-review__spin"
        >
          <div className="optimisation-review__body">
            <SelectedSpecifications
              searchQuery={searchQuery}
              searchOptions={searchOptions}
              searchDropdownOpen={searchDropdownOpen}
              searchLoading={searchLoading}
              visibleSelectedRows={visibleSelectedRows}
              previewChipRows={previewChipRows}
              hasMoreChips={hasMoreChips}
              viewAllOpen={viewAllOpen}
              setViewAllOpen={setViewAllOpen}
              handleClearAll={handleClearAll}
              handleRemoveChip={handleRemoveChip}
              handleLiveSearchChange={handleLiveSearchChange}
              handleSelectSearchOption={handleSelectSearchOption}
              setOpenReviewFilter={setOpenReviewFilter}
              setSearchQuery={setSearchQuery}
              setSearchOptions={setSearchResults}
              setSearchDropdownOpen={setSearchDropdownOpen}
            />

            <CostBenefitAnalysis
              formState={formState}
              setFormState={setFormState}
              slidersEnabled={slidersEnabled}
              markDirty={handleSlidersDirty}
              reviewRows={reviewRows}
            />

            <SelectedSpecificationsSummary
              showSummarySection={showSummarySection}
              summaryExpanded={summaryExpanded}
              setSummaryExpanded={setSummaryExpanded}
              allSummarySelected={allSummarySelected}
              summarySelectedKeys={summarySelectedKeys}
              setSummarySelectedKeys={setSummarySelectedKeys}
              summaryRows={summaryRows}
              summaryPage={summaryPage}
              summaryPageSize={summaryPageSize}
              setSummaryPage={setSummaryPage}
              setSummaryPageSize={setSummaryPageSize}
              summarySearchQuery={summarySearchQuery}
              onSearchQueryChange={handleSummarySearch}
              summaryTotal={listSummaryRows.length > 0 ? listSummaryRows.length : summaryTotal}
            />

            <OptimisationWorkflowForm
              hasCalculated={hasCalculated}
              formState={formState}
              setFormState={setFormState}
              assignToOptions={assignToOptions}
              disabled={summarySelectedKeys.length === 0}
              handleStatusChange={handleStatusChange}
              handleSaveAssignment={handleSaveAssignment}
              handleClearAssignment={handleClearAssignment}
              handleSaveJustification={handleSaveJustification}
              handleClearJustification={handleClearJustification}
            />
          </div>
        </Spin>

        <OptimisationReviewFooter
          handleCancel={handleCancel}
          calculateSavings={handleCalculateOrApply}
          hasSelectedRows={hasSelectedRows}
          hasCalculated={hasCalculated}
          needsRecalc={needsRecalc}
          spinning={spinning}
          downloadReport={downloadReport}
        />
      </div>

      <RankingFilters
        open={openReviewFilter}
        setOpen={setOpenReviewFilter}
        scope="optimisation"
        filteredValues={reviewFilterValues}
        messageToast={messageToast}
        onOptimisationApply={(rows, filters) => {
          setReviewFilterValues(filters);
          setExcludedRowIds([]);
          setReviewRows(rows || []);
          setHasCalculated(false);
          setNeedsRecalc(false);
          setSummarySelectedKeys([]);
          setViewAllOpen(false);
          setReviewId(null);
          if (!rows || rows.length === 0) {
            resetFormMetrics();
          }
          // Intentionally NOT propagating to the ranking grid: the modal's
          // filter should only affect the review's selected specifications,
          // not the underlying Rankings table.
          messageToast?.(
            "success",
            rows?.length
              ? `${rows.length} specification${rows.length === 1 ? "" : "s"} loaded from filters.`
              : "Filters applied. No matching specifications found.",
          );
        }}
        onOptimisationClear={(filters, clearedSomething) => {
          setReviewFilterValues(filters || FILTER_INITIAL_OBJECT);
          // Nothing entered means nothing to clear, so skip the toast rather
          // than report success for a no-op.
          if (clearedSomething) {
            messageToast?.("success", "Optimization Review filters cleared.");
          }
        }}
      />
    </Modal>
  );
}
